/**
 * Patient Transfer workflow — backs /clinical-transfers on the backend (PatientTransferController).
 * Ongoing care itself moves, distinct from Referral (patient stays with the referring provider —
 * see modules/clinicalReferrals.ts). ClinVea-network destinations only; external transfer delivery
 * is not built.
 */
import { fetchApi } from '../client';
import { buildQS } from '../helpers';
import type { ClinicalUrgency, RecordShareCategory, RecordShareSelection, DestinationClinic, DestinationDoctor } from './clinicalReferrals';

export type TransferType = 'CARE_TRANSFER' | 'EMERGENCY_TRANSFER' | 'FACILITY_TRANSFER' | 'PROVIDER_TRANSFER' | 'RECORD_TRANSFER';

export type TransferStatus =
  | 'DRAFT' | 'REQUESTED' | 'EMERGENCY_INITIATED' | 'RECEIVING_FACILITY_CONTACTED' | 'SENT'
  | 'RECEIVED' | 'UNDER_REVIEW' | 'ACCEPTED' | 'DECLINED' | 'TRANSPORT_PENDING' | 'TRANSPORT_ARRANGED'
  | 'PATIENT_DEPARTED' | 'PATIENT_ARRIVED' | 'HANDOFF_COMPLETED' | 'COMPLETED' | 'CANCELLED';

export interface TransferTransport {
  transportType?: string;
  receivingFacilityName?: string;
  receivingFacilityPhone?: string;
  receivingFacilityContactedAt?: string;
  transportArrangedAt?: string;
  departedAt?: string;
  arrivedAt?: string;
  notes?: string;
}

export interface TransferHandoff {
  vitalsSummary?: string;
  treatmentProvided?: string;
  medicationsAdministered?: string;
  allergies?: string;
  relevantLabs?: string;
  relevantImaging?: string;
  clinicalConcerns?: string;
  sendingProviderId?: string;
  sendingProviderName?: string;
  receivingProviderId?: string;
  receivingProviderName?: string;
  handoffConfirmedBy?: string;
  handoffConfirmedAt?: string;
}

export interface Transfer {
  id: string;
  transferNumber: string;
  patientId: string;
  patientName?: string;
  patientMobile?: string;
  patientDateOfBirth?: string;
  patientAge?: number;
  patientGender?: string;

  initiatingClinicId: string;
  initiatingClinicName?: string;
  initiatingProviderId?: string;
  initiatingProviderName?: string;

  receivingOrganizationId: string;
  receivingOrganizationName?: string;
  receivingProviderId?: string;
  receivingProviderName?: string;

  transferType: TransferType;
  urgency: ClinicalUrgency;
  reason: string;
  clinicalSummary?: string;
  currentCondition?: string;
  transportType?: string;
  status: TransferStatus;

  requestedAt: string;
  acceptedAt?: string;
  declinedAt?: string;
  departedAt?: string;
  arrivedAt?: string;
  handoffCompletedAt?: string;
  completedAt?: string;
  cancelledAt?: string;

  incoming: boolean;
  allowedNextStatuses: TransferStatus[];
  recordShare?: RecordShareSelection | null;
  transport?: TransferTransport | null;
  handoff?: TransferHandoff | null;
}

export interface TransferDashboard {
  outgoingTotal: number;
  outgoingPendingDecision: number;
  outgoingInTransit: number;
  outgoingCompleted: number;
  incomingTotal: number;
  incomingNeedsReview: number;
  incomingAccepted: number;
  incomingAwaitingHandoff: number;
  emergencyOpen: number;
}

export interface TransferStatusHistoryEntry {
  id: string;
  transferId: string;
  fromStatus: TransferStatus | null;
  toStatus: TransferStatus;
  changedBy: string;
  reason?: string | null;
  changedAt: string;
}

export interface TransferCreateRequest {
  patientId: string;
  initiatingProviderId?: string;
  receivingOrganizationId: string;
  receivingProviderId?: string;
  transferType: TransferType;
  urgency: ClinicalUrgency;
  reason: string;
  clinicalSummary?: string;
  currentCondition?: string;
  transportType?: string;
  recordShareCategories?: RecordShareCategory[];
  recordShareDateFrom?: string;
  recordShareDateTo?: string;
  submit: boolean;
}

export interface TransferTransportRequest {
  transportType?: string;
  receivingFacilityName?: string;
  receivingFacilityPhone?: string;
  notes?: string;
}

export interface TransferHandoffRequest {
  vitalsSummary?: string;
  treatmentProvided?: string;
  medicationsAdministered?: string;
  allergies?: string;
  relevantLabs?: string;
  relevantImaging?: string;
  clinicalConcerns?: string;
  receivingProviderId?: string;
}

const BASE = '/api/clinical-transfers';

export const clinicalTransfersModule = {
  listOutgoing: (status?: TransferStatus) =>
    fetchApi<Transfer[]>(`${BASE}${buildQS({ status })}`),

  listIncoming: () =>
    fetchApi<Transfer[]>(`${BASE}/incoming`),

  getDashboard: () =>
    fetchApi<TransferDashboard>(`${BASE}/dashboard`),

  searchDestinationClinics: (q: string) =>
    fetchApi<DestinationClinic[]>(`${BASE}/destinations/clinics${buildQS({ q })}`),

  listDestinationDoctors: (destinationClinicId: string) =>
    fetchApi<DestinationDoctor[]>(`${BASE}/destinations/clinics/${destinationClinicId}/doctors`),

  get: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}`),

  getTimeline: (id: string) =>
    fetchApi<TransferStatusHistoryEntry[]>(`${BASE}/${id}/timeline`),

  create: (data: TransferCreateRequest) =>
    fetchApi<Transfer>(BASE, { method: 'POST', body: JSON.stringify(data) }),

  submit: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/submit`, { method: 'POST' }),

  markFacilityContacted: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/facility-contacted`, { method: 'POST' }),

  accept: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/accept`, { method: 'POST' }),

  decline: (id: string, reason?: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/decline`, { method: 'POST', body: JSON.stringify({ reason }) }),

  markTransportReady: (id: string, data?: TransferTransportRequest) =>
    fetchApi<Transfer>(`${BASE}/${id}/transport-ready`, { method: 'POST', body: JSON.stringify(data ?? {}) }),

  markDeparted: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/departed`, { method: 'POST' }),

  markArrived: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/arrived`, { method: 'POST' }),

  confirmHandoff: (id: string, data?: TransferHandoffRequest) =>
    fetchApi<Transfer>(`${BASE}/${id}/handoff`, { method: 'POST', body: JSON.stringify(data ?? {}) }),

  complete: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/complete`, { method: 'POST' }),

  cancel: (id: string, reason?: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),

  getRecordShare: (id: string) =>
    fetchApi<RecordShareSelection | null>(`${BASE}/${id}/record-share`),

  setRecordShare: (id: string, data: { categories: RecordShareCategory[]; dateFrom?: string; dateTo?: string }) =>
    fetchApi<RecordShareSelection>(`${BASE}/${id}/record-share`, { method: 'POST', body: JSON.stringify(data) }),
};
